import type { View } from "@gooner/shared";
import { Pager, usePaged } from "./Pager";
import { PresetThumb } from "./PresetThumb";
import { IconCopy, IconPencil, IconTrash } from "../ui/icons";
import { MASK, maskIf } from "../../lib/replayMask";

/** Rows are short, so more of them fit than the 3x2 grid this replaces. */
const PAGE_SIZE = 5;

/**
 * Your other views, listed under the layout picker as wide, short rows.
 *
 * The shape is the point. These were square cards in the same 3x2 grid the quick setups
 * use, which said they were the same KIND of thing — a menu of shapes to apply. They are
 * not: they are the other views you own, each of which you might want to go work on
 * rather than consume. A wide row has somewhere to put that distinction, and a card that
 * is mostly a title does not.
 *
 * **Two actions, and they are opposites**, which is the whole reason this isn't a
 * single-click card:
 *
 *   • Edit (and clicking the row itself) LEAVES — it opens that view in this editor, and
 *     the view you were on is untouched. Safe. Navigating flushes the outgoing view's
 *     debounced save first (see ViewEditor's route effect), so nothing in flight is lost.
 *   • Copy STAYS — it pours that view's configuration into the one you are editing, over
 *     the top of whatever is there. Destructive, and it asks first.
 *
 * A single-click card had to pick one of those, and it picked Copy: clicking a saved view
 * silently replaced everything on your canvas. The gentler of the two actions is now the
 * one you get by clicking anywhere, and the destructive one costs a deliberate aim at a
 * small button plus a confirmation.
 *
 * Delete stays hover-revealed and last, unchanged in behaviour and deliberately furthest
 * from the row's own click target.
 */
export function ViewRowList({
  views,
  onOpen,
  onCopy,
  onDelete,
}: {
  views: readonly View[];
  /** Go edit this view — the row's own click and the pencil both land here. */
  onOpen: (view: View) => void;
  /** Pour this view into the one being edited. The caller owns the confirmation. */
  onCopy: (view: View) => void;
  onDelete: (view: View) => void;
}) {
  const { page, pages, setPage, shown } = usePaged(views, PAGE_SIZE);

  return (
    <div className="space-y-2">
      <ul className="space-y-1.5">
        {shown.map((view) => {
          const n = view.regions.length;
          return (
            <li key={view.id} className="group relative">
              {/*
                The row itself is the Edit button, stretched. The two controls on the
                right are siblings rather than children of it, because a button inside a
                button is invalid and the nesting is what would make Copy also fire Edit.
              */}
              <button
                type="button"
                onClick={() => onOpen(view)}
                className="flex w-full items-center gap-2.5 rounded-lg border border-[var(--color-edge)] bg-[var(--color-panel)]/70 p-2 pr-[4.75rem] text-left transition-colors hover:border-[var(--color-accent)]/60"
              >
                {/* Sized by a wrapper rather than by PresetThumb's own className:
                    the thumb is `w-full` internally, and passing a width alongside it
                    is two utilities of equal specificity fighting over one property. */}
                <span className="block w-14 shrink-0">
                  <PresetThumb rects={view.regions} aspect={view.aspect} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden className="shrink-0 text-sm leading-none">
                      {view.icon || "🖥️"}
                    </span>
                    {/* The view's name is the person's: masked in session replay
                        (lib/replayMask), and the hover title that names it rides on the
                        same masked span, so the region count beside it stays readable. */}
                    <span
                      className="truncate text-sm font-semibold text-zinc-100"
                      title={`Open ${view.name} in the editor`}
                      {...MASK}
                    >
                      {view.name}
                    </span>
                  </span>
                  <span className="mt-0.5 block text-[11px] text-zinc-500">
                    {n} {n === 1 ? "region" : "regions"}
                  </span>
                </span>
              </button>

              {/* Pencil then copy, in that order, pinned right. Both are always visible:
                  they are the row's reason for existing, and hiding them until hover
                  would leave a list of rows whose only apparent action is the one that
                  navigates away. */}
              <div className="pointer-events-none absolute inset-y-0 right-1.5 flex items-center gap-0.5">
                <RowAction
                  label={`Open ${view.name} in the editor`}
                  mask
                  onClick={() => onOpen(view)}
                >
                  <IconPencil width={13} height={13} />
                </RowAction>
                <RowAction
                  label={`Copy ${view.name} into the view you are editing`}
                  mask
                  onClick={() => onCopy(view)}
                >
                  <IconCopy width={13} height={13} />
                </RowAction>
                <RowAction
                  label={`Delete ${view.name}`}
                  mask
                  onClick={() => onDelete(view)}
                  // Hidden until the row is hovered or the button itself is focused, so
                  // keyboard users can still reach it.
                  className="text-zinc-600 opacity-0 hover:text-red-400 focus:opacity-100 group-hover:opacity-100"
                >
                  <IconTrash width={13} height={13} />
                </RowAction>
              </div>
            </li>
          );
        })}
      </ul>

      <Pager page={page} pages={pages} onPage={setPage} label="views" />
    </div>
  );
}

/** One of the small controls pinned to a row's right edge. `pointer-events-auto` undoes
 *  the container's opt-out, which is what keeps the strip from swallowing clicks meant
 *  for the row underneath it. */
function RowAction({
  label,
  onClick,
  className,
  mask = false,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  /** The label names something a person wrote (a view): mask its aria-label and title in
   *  replay. The button is icon-only, so nothing visible is lost. */
  mask?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...maskIf(mask)}
      onClick={onClick}
      className={`pointer-events-auto rounded-md p-1.5 text-zinc-500 transition-colors hover:bg-[var(--color-ink)]/70 hover:text-zinc-200 focus:outline-none ${className ?? ""}`}
    >
      {children}
    </button>
  );
}
